/**
 * SOLID Review
 * S — Strong. One concern: map the websocket's state at a table to the reconnecting/disconnected overlays.
 * O — Acceptable. A new connection state is one more arm of the mapping.
 * L — Strong. A view model (`IServiceViewModel`) with no state of its own: the overlays are its port's.
 * I — Strong. IPortDisconnectionOverlay has four methods, all "show/hide an overlay".
 * D — Strong. The overlay port it is handed and the view's connection-state type.
 */
import type { TClientViewState } from '../../application/ports/ClientStateView';
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions, type IConfigReaction } from './Reactions';

type ConnectionState = TClientViewState['connectionState'];

/** The overlays a disconnection shows — the screen's, which the monitor tells what to show. */
export interface IPortDisconnectionOverlay {
  showReconnecting(): void;
  hideReconnecting(): void;
  showDisconnected(): void;
  hideDisconnected(): void;
}

/** What the monitor's view hands it: the socket's state and the table it is at. */
export type TMonitorInputs = readonly [connectionState: ConnectionState, roomId: string | null];

const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * THE DISCONNECTION MONITOR (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useDisconnectionMonitor.ts`'s effect, now a
 * reaction: at a table, the socket's state shows and hides the overlays; away from one (the welcome screen) a reconnection is silent.
 */
export class DisconnectionMonitor implements IServiceViewModel<typeof NO_STATE> {
  private readonly declared: ReadonlyArray<IConfigReaction<TMonitorInputs>> = [{
    deps: (inputs) => inputs,
    run: ([connectionState, roomId]) => {
      if (!roomId) return;
      if (connectionState === 'reconnecting') {
        this.port.showReconnecting();
        this.port.hideDisconnected();
      } else if (connectionState === 'failed') {
        this.port.hideReconnecting();
        this.port.showDisconnected();
      } else if (connectionState === 'connected') {
        this.port.hideReconnecting();
        this.port.hideDisconnected();
      }
    },
  }];
  private readonly reactions = new Reactions<TMonitorInputs>(this.declared);

  constructor(private readonly port: IPortDisconnectionOverlay) {}

  /** Its reaction over the facts of the screen's model that composes it (AppRoot's, P3): the socket's state and the table. */
  reactionsOver<TFacts>(factsOf: (facts: TFacts) => TMonitorInputs): Array<IConfigReaction<TFacts>> {
    return Reactions.over(this.declared, factsOf);
  }

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TMonitorInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TMonitorInputs);
  }
}
